import React, { useRef, useEffect, useCallback } from 'react';
import Button from './Button';
import MaterialIcon from '../elements/MaterialIcon';
import { holdBackButton } from '../../utilities/backButtonClose.js';

// Where materialize's open animation leaves the modal: it animates `top` to this, and nothing else sets it.
const ENDING_TOP = 0.1;
const GROW_DURATION = 250;
const GROW_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

/** Whether the pointer is a mouse rather than a finger. */
function hasFinePointer() {
	try {
		return !!window.matchMedia && window.matchMedia('(pointer: fine)').matches;
	} catch(error) {
		return false;
	}
}

/**
 * The transform that shrinks the modal onto `origin`, or null to leave materialize's own animation
 * alone: no origin, an origin that has been removed or scrolled out of sight, no Web Animations
 * (jsdom), or a reader who asked for reduced motion.
 *
 * The modal's resting box is computed rather than measured, because materialize's own animation
 * has it mid-scale when this runs. It is centred by `margin: auto` and sits at ENDING_TOP.
 */
function transformOnto(modal, origin) {
	if(!origin || !origin.isConnected || typeof modal.animate !== 'function') {
		return null;
	}
	if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return null;
	}
	const from = origin.getBoundingClientRect();
	if(!from.width || !from.height || from.bottom < 0 || from.top > window.innerHeight) {
		return null;
	}
	const width = modal.offsetWidth;
	const height = modal.offsetHeight;
	if(!width || !height) {
		return null;
	}
	const left = (document.documentElement.clientWidth - width) / 2;
	const top = window.innerHeight * ENDING_TOP;
	const dx = from.left + from.width / 2 - (left + width / 2);
	const dy = from.top + from.height / 2 - (top + height / 2);
	return `translate(${dx}px, ${dy}px) scale(${from.width / width}, ${from.height / height})`;
}

Modal.defaultProps = {
	onCloseStart: (modal) => {
	},
	onClose: () => {
	},
	isOpen: true,
	closeOnBack: true,
};
/**
 * `origin`, a DOM element, makes the modal grow out of it as it opens and shrink back into it as it
 * closes. It is read at those two moments, so an element that has gone by the time the modal closes
 * just gets the usual fade. A Web Animation runs over materialize's own, which it outranks for the
 * transform, and materialize is told not to move the modal vertically so the two agree on where it
 * ends up.
 *
 * `children` may be a function, which is given `close`: closing through it runs the close animation
 * before `onClose`, where unmounting the modal from outside cuts it off.
 *
 * Materialize focuses the modal itself as it opens, taking focus from anything inside that grabbed
 * it on mounting, so an element marked `data-autofocus` is given it back once the modal is open.
 * `data-autofocus='fine-pointer'` does so only with a mouse: on a touchscreen, focusing a field opens
 * the keyboard over the form, which is wanted where typing is the point of opening it and not where
 * the field is one of several to look over.
 *
 * The browser's back button closes an open modal, through the same animation as its close button.
 * However it closes, a field inside that still has focus is blurred first, so one that saves when
 * it is left saves.
 * Pass `closeOnBack={false}` for a modal whose open state is already in the url: its own history
 * entry does that job, and a second one on top lands Back on the url that reopens it.
 *
 * `onBack`, given, draws a back arrow before the header, for a modal showing a step inside itself.
 * It only draws the arrow: the browser's Back stepping back too is the caller's own hold.
 */
export default function Modal(props) {
	const { children, footer, header, buttons, className, coordinates, isOpen, origin, onBack } = props;
	const element = useRef(null);
	const instance = useRef(null);
	const originRef = useRef(origin);
	originRef.current = origin;
	const growth = useRef(null);
	const releaseBack = useRef(null);

	const letGoOfBack = () => {
		if(releaseBack.current) {
			releaseBack.current();
			releaseBack.current = null;
		}
	};

	function popItOut() {

	}

	const closeClick = useCallback(() => {
		if(instance.current) {
			instance.current.close();
		}
	}, []);


	useEffect(() => {
		if(isOpen) {
			const { opacity, onClose, onCloseStart } = props;
			const options = {
				// An explicit undefined replaces materialize's 0.5, and the page was never dimmed.
				opacity: opacity === undefined ? 0.5 : opacity,
				onCloseEnd: (modal) => {
					letGoOfBack();
					if(growth.current) {
						growth.current.cancel();
						growth.current = null;
					}
					onClose(modal);
				},
				onCloseStart: (modal) => {
					// A field saved when it is left would otherwise be unmounted still focused, unsaved,
					// by a close that never moved focus: Back, Esc, or a close from code.
					const focused = document.activeElement;
					if(focused && element.current && element.current.contains(focused) && typeof focused.blur === 'function') {
						focused.blur();
					}
					const onto = transformOnto(element.current, originRef.current);
					if(onto) {
						growth.current = element.current.animate(
							[{ transform: 'none', opacity: 1 }, { transform: onto, opacity: 0 }],
							{ duration: GROW_DURATION, easing: GROW_EASING, fill: 'forwards' },
						);
					}
					onCloseStart(modal);
				},
			};
			if(originRef.current) {
				options.startingTop = `${ENDING_TOP * 100}%`;
				options.endingTop = `${ENDING_TOP * 100}%`;
				options.inDuration = GROW_DURATION;
				options.outDuration = GROW_DURATION;
			}
			instance.current = M.Modal.init(element.current, options);
			const timeout = setTimeout(() => {
				instance.current.open();
				if(props.closeOnBack) {
					releaseBack.current = holdBackButton(() => {
						releaseBack.current = null;
						instance.current.close();
					});
				}
				const autofocus = element.current.querySelector('[data-autofocus]');
				if(autofocus && (autofocus.dataset.autofocus !== 'fine-pointer' || hasFinePointer())) {
					autofocus.focus();
				}
				const from = transformOnto(element.current, originRef.current);
				if(from) {
					element.current.animate(
						[{ transform: from, opacity: 0 }, { transform: 'none', opacity: 1 }],
						{ duration: GROW_DURATION, easing: GROW_EASING },
					);
				}
			}, 0);
			return () => {
				instance.current.close();
				clearTimeout(timeout);
				letGoOfBack();
			};
		} else {
			console.log('closing', instance.current);
			if(instance.current) {
				instance.current.close();
			}
		}
	}, [isOpen]);

	const style = {};
	if(coordinates) {
		style.top = coordinates.top || 'auto';
		style.left = coordinates.left || 'auto';
		style.right = coordinates.right || 'auto';
		style.bottom = coordinates.bottom || 'auto';
	}
	// materialize sets `display: block` on the modal itself, so the layout lives in a frame inside it:
	// the header and footer stay put and only the content between them scrolls.
	return (
		<div
			ref={element}
			className={`modal ${className || ''}`}
			style={style}
		>
			<div className='modal-frame'>
				{header &&
				<div className='modal-header'>
					{onBack &&
					<button type='button' className='btn-flat modal-header__back' aria-label='Back' onClick={onBack}>
						<MaterialIcon>arrow_back</MaterialIcon>
					</button>
					}
					<h4>{header}</h4>
					<button type='button' className='btn-flat modal-header__close' aria-label='Close' onClick={closeClick}>
						<MaterialIcon>close</MaterialIcon>
					</button>
				</div>
				}
				<div className='modal-content'>
					{typeof children === 'function' ? children(closeClick) : children}
				</div>
				{footer &&
				<div className='modal-footer'>
					{footer}
				</div>
				}
				{buttons && buttons.length > 0 &&
				<div className='modal-footer'>
					{buttons.map((button) => {
						const { text, ...props } = button;
						return (
							<Button
								key={button.key || button.text}
								flat
								{...props}
							>
								{text}
							</Button>
						);
					})}
				</div>
				}
			</div>
		</div>
	);
}
